import React from "react";

interface Consultant {
  id: string;
  cv_id?: number;  // Add cv_id
  firstname: string;
  lastname: string;
  email: string;
  cv_title?: string;
  main_skills?: { name: string, functional_skills: string[] }[];
  sector?: string;
}

interface ConsultantCardProps {
  consultant: Consultant;
}

const ConsultantCard: React.FC<ConsultantCardProps> = ({ consultant }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  
  const handleViewCV = () => {
    if (consultant.cv_id) {
      // Open CV in new tab using the CV generation endpoint
      window.open(`${API_URL}/cv/generate/${consultant.cv_id}/pdf`, '_blank');
    } else {
      alert("Ce consultant n'a pas encore de CV enregistré.");
    }
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        padding: "1.5rem",
        boxShadow: "0 4px 24px rgba(30, 60, 115, 0.08)",
        border: "2px solid transparent",
        transition: "all 0.3s ease",
        cursor: "pointer",
        animation: "fadeIn 0.4s ease-out",
      }}
      onClick={handleViewCV}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#FF9100";
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 8px 32px rgba(30, 60, 115, 0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "transparent";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 24px rgba(30, 60, 115, 0.08)";
      }}
    >
      {/* Header - Avatar & Name */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        {/* Avatar */}
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #1E3C73 0%, #2a5298 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "1.5rem",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {consultant.firstname?.charAt(0).toUpperCase() || "?"}
          {consultant.lastname?.charAt(0).toUpperCase() || ""}
        </div>

        {/* Name & Email */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              fontSize: "1.125rem",
              fontWeight: 700,
              color: "#1E3C73",
              margin: "0 0 0.25rem 0",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {consultant.firstname} {consultant.lastname}
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#64748b",
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {consultant.email}
          </p>
        </div>
      </div>

      {/* CV Title */}
      {consultant.cv_title && (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem",
            background: "#f8fafc",
            borderRadius: "8px",
            borderLeft: "3px solid #FF9100",
          }}
        >
          <p
            style={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#1E3C73",
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            {consultant.cv_title}
          </p>
        </div>
      )}

      {/* Sector */}
      {consultant.sector && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 0.75rem",
            background: "#e0f2fe",
            color: "#0369a1",
            borderRadius: "8px",
            fontSize: "0.875rem",
            fontWeight: 500,
            marginBottom: "1rem",
          }}
        >
          <span>🏢</span>
          <span>{consultant.sector}</span>
        </div>
      )}

      {/* Skills */}
      {consultant.main_skills && consultant.main_skills.length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          <p
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "0.5rem",
            }}
          >
            Compétences principales
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
            }}
          >
            {consultant.main_skills.slice(0, 5).map((skill, index) => (
              <span
                key={index}
                style={{
                  padding: "0.375rem 0.75rem",
                  background: "#fff7ed",
                  color: "#c2410c",
                  borderRadius: "6px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  border: "1px solid #fed7aa",
                }}
              >
                {skill.name}
              </span>
            ))}

            {consultant.main_skills.length > 5 && (
              <span
                style={{
                  padding: "0.375rem 0.75rem",
                  background: "#f1f5f9",
                  color: "#64748b",
                  borderRadius: "6px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                +{consultant.main_skills.length - 5}
              </span>
            )}
          </div>
        </div>
      )}

      {/* View CV Button */}
      <button
        style={{
          width: "100%",
          padding: "0.75rem",
          background: "linear-gradient(135deg, #FF9100 0%, #ffb347 100%)",
          color: "white",
          border: "none",
          borderRadius: "10px",
          fontSize: "0.95rem",
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(255, 145, 0, 0.2)",
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleViewCV();
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 145, 0, 0.3)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(255, 145, 0, 0.2)";
        }}
      >
        <span>📄</span>
        <span>Voir le CV</span>
      </button>
    </div>
  );
};

export default ConsultantCard;
